import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, ShoppingBag, Heart, Package, Award, Users, FileText, Sparkles } from 'lucide-react';
import api from '../../utils/api';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import ProductCard from '../../components/common/ProductCard';
import PriceCategories from '../../components/common/PriceCategories';
import useWhatsAppNumber from '../../hooks/useWhatsAppNumber';
import { cldImg } from '../../utils/cloudinary';
import { getPageCache, setPageCache } from '../../utils/pageCache';
import { rememberAnchor } from '../../utils/scrollMemory';

// ── WhatsApp brand logo (lucide ships no brand icons) ──────────
const WhatsAppIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} style={style} aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.515 5.26l-.999 3.648 3.973-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
  </svg>
);

// Neutral skeleton placeholders (no fake data) used to hold layout while the
// real content loads in from the database.
const SKELETON_ITEMS = Array.from({ length: 8 }, (_, i) => ({ id: `sk-${i}`, skeleton: true }));

const DEAL_END = new Date(Date.now() + 8 * 3600 * 1000).toISOString();

// ── Countdown Timer ────────────────────────────────────────────
function useCountdown(endTime) {
  const calc = () => {
    const diff = new Date(endTime) - new Date();
    if (diff <= 0) return { h: '00', m: '00', s: '00' };
    return {
      h: String(Math.floor(diff / 3600000)).padStart(2, '0'),
      m: String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0'),
      s: String(Math.floor((diff % 60000) / 1000)).padStart(2, '0'),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const t = setInterval(() => setTime(calc()), 1000); // eslint-disable-line react-hooks/exhaustive-deps
    return () => clearInterval(t);
  }, [endTime]); // eslint-disable-line react-hooks/exhaustive-deps
  return time;
}

// ── Hero Slider ────────────────────────────────────────────────
const HERO_SLIDES = [
  { bg: 'from-brand-cream to-brand-yellow-light', title: 'All the anime merch', highlight: 'you deserve', sub: 'Premium quality, curated with love', emoji: '🎌' },
  { bg: 'from-brand-dark to-gray-800', title: 'New arrivals', highlight: 'just dropped', sub: 'Shop the latest anime figures & collectibles', emoji: '⭐', dark: true },
  { bg: 'from-orange-50 to-brand-cream', title: 'Custom orders', highlight: 'made with love', sub: 'Personalized gifts, cake toppers & more', emoji: '🎁' },
];

function HeroSlider({ apiBanners, loading }) {
  const [idx, setIdx] = useState(0);
  const slides = apiBanners.length ? apiBanners : null;
  const touchX = useRef(null);

  const count = slides ? slides.length : HERO_SLIDES.length;

  // Auto-advance. Depending on `idx` restarts the timer on every change, so a
  // manual swipe/tap gives a fresh full interval instead of snapping back to
  // the previous slide a moment later.
  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % count), 4500);
    return () => clearInterval(t);
  }, [count, idx]);

  // Keep idx valid if the slide count changes after banners load.
  useEffect(() => { setIdx(i => (i >= count ? 0 : i)); }, [count]);

  // While banners are still loading, show a neutral skeleton instead of the
  // placeholder marketing slides — otherwise they flash and get swapped out
  // the instant real banners arrive (the "glitch on load").
  if (loading && !slides) {
    return (
      <div className="relative w-full md:px-6 md:py-3" style={{ minHeight: 'clamp(160px, 26vw, 380px)' }}>
        <div className="rounded-b-2xl md:rounded-2xl bg-brand-cream-light animate-pulse"
          style={{ height: 'clamp(160px, 26vw, 380px)' }} />
      </div>
    );
  }

  const goTo = (n) => setIdx(((n % count) + count) % count);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) goTo(dx < 0 ? idx + 1 : idx - 1);
    touchX.current = null;
  };

  return (
    <div className="relative w-full md:px-6 md:py-3" style={{ minHeight: 'clamp(160px, 26vw, 380px)' }}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {slides ? (
        slides.map((banner, i) => (
          <div key={banner.id} className={`relative rounded-b-2xl md:rounded-2xl overflow-hidden transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'hidden'}`}
            style={{ height: 'clamp(160px, 26vw, 380px)' }}>
            <picture>
              {banner.mobile_image_url && <source media="(max-width: 767px)" srcSet={cldImg(banner.mobile_image_url, 768)} />}
              <img src={cldImg(banner.image_url, 1400)} alt={banner.title}
                className="w-full h-full object-cover object-center"
                fetchpriority={i === 0 ? 'high' : 'auto'}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async" />
            </picture>
            {(banner.title || banner.subtitle || banner.button_text) && (
              <div className="absolute inset-0 flex items-center px-6 md:px-14"
                style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)' }}>
                <div>
                  {banner.subtitle && <p className="text-xs md:text-sm font-bold uppercase tracking-widest text-white/80 mb-2">{banner.subtitle}</p>}
                  {banner.title && <h1 className="text-xl md:text-4xl font-black text-white mb-4 leading-tight drop-shadow-lg max-w-xs md:max-w-lg">{banner.title}</h1>}
                  {banner.link && <Link to={banner.link} className="btn-primary rounded-full px-6 py-2.5 text-sm md:text-base inline-block">{banner.button_text || 'Shop Now'}</Link>}
                </div>
              </div>
            )}
          </div>
        ))
      ) : (
        HERO_SLIDES.map((slide, i) => (
          <div key={i} className={`absolute inset-0 bg-gradient-to-br ${slide.bg} transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0 pointer-events-none'} flex items-center`} style={{ minHeight: 'clamp(160px, 26vw, 380px)' }}>
            <div className="max-w-7xl mx-auto px-8 md:px-16 w-full flex items-center justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-brand-yellow mb-2">TheYellove Store</p>
                <h1 className={`text-3xl md:text-6xl font-black mb-2 leading-tight ${slide.dark ? 'text-white' : 'text-brand-dark'}`}>
                  {slide.title}<br /><span className="text-brand-yellow">{slide.highlight}</span>
                </h1>
                <p className={`text-sm md:text-base mb-6 ${slide.dark ? 'text-gray-300' : 'text-gray-500'}`}>{slide.sub}</p>
                <Link to="/products" className="btn-primary rounded-full px-8 py-3 text-base inline-block">Shop Now</Link>
              </div>
              <div className="hidden md:flex text-9xl select-none">{slide.emoji}</div>
            </div>
          </div>
        ))
      )}

      {/* Arrows */}
      {count > 1 && <>
        <button onClick={() => setIdx(i => (i - 1 + count) % count)}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all z-10">
          <ChevronLeft className="w-5 h-5 text-brand-dark" />
        </button>
        <button onClick={() => setIdx(i => (i + 1) % count)}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all z-10">
          <ChevronRight className="w-5 h-5 text-brand-dark" />
        </button>
      </>}

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {Array.from({ length: count }).map((_, i) => (
          <button key={i} onClick={() => setIdx(i)}
            className={`rounded-full transition-all duration-300 ${i === idx ? 'w-6 h-2 bg-brand-yellow' : 'w-2 h-2 bg-white/60'}`} />
        ))}
      </div>
    </div>
  );
}

// ── Section Header ─────────────────────────────────────────────
function SectionHeader({ label, title, viewAllHref }) {
  return (
    <div className="flex items-end justify-between mb-5">
      <div>
        {label && <p className="text-xs font-black text-brand-yellow uppercase tracking-widest mb-1">{label}</p>}
        <h2 className="section-heading">{title}</h2>
      </div>
      {viewAllHref && (
        <Link to={viewAllHref} className="text-sm font-semibold text-brand-dark hover:text-brand-yellow transition-colors flex items-center gap-1">
          View All <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

// ── Category Icons Row ─────────────────────────────────────────
function CategoryRow({ categories, loading }) {
  const scrollRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);
  // While categories load, show neutral skeleton circles (same dimensions) so
  // the row holds its height — no fake data, and the page below doesn't jump.
  const showSkeleton = loading && !categories.length;
  const items = showSkeleton ? SKELETON_ITEMS : categories;
  const cols = Math.ceil(items.length / 2); // total columns
  const visibleCols = 3; // ~3 columns visible at once
  const totalDots = Math.ceil(cols / visibleCols);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const progress = el.scrollLeft / (el.scrollWidth - el.clientWidth);
    setActiveDot(Math.round(progress * (totalDots - 1)));
  };

  // Loaded and genuinely empty — render nothing.
  if (!items.length) return null;

  return (
    <section className="py-5 border-b border-gray-100">
      {/* Mobile: 2-row horizontal scroll with peek hint */}
      <div ref={scrollRef} onScroll={handleScroll} className="md:hidden overflow-x-auto scrollbar-hide pl-3 pr-6">
        <div className="grid grid-rows-2 grid-flow-col gap-x-3 gap-y-3 w-max">
          {items.map(cat => cat.skeleton ? (
            <div key={cat.id} className="flex flex-col items-center gap-1.5 w-24">
              <div className="w-20 h-20 rounded-full bg-gray-100 animate-pulse" />
              <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
            </div>
          ) : (
            <Link key={cat.id} to={`/category/${cat.slug || cat.id}`}
              className="flex flex-col items-center gap-1.5 group w-24">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-brand-cream-light border-2 border-brand-yellow/20 group-hover:border-brand-yellow transition-all duration-300">
                {cat.image
                  ? <img src={cldImg(cat.image, 300)} alt={cat.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-3xl">{cat.emoji || '🎁'}</div>
                }
              </div>
              <span className="text-xs text-center text-gray-700 font-semibold leading-tight w-full">{cat.name}</span>
            </Link>
          ))}
        </div>
      </div>
      {/* Scroll dots */}
      {!showSkeleton && totalDots > 1 && (
        <div className="md:hidden flex justify-center gap-1.5 mt-3">
          {Array.from({ length: totalDots }).map((_, i) => (
            <span key={i} className={`rounded-full transition-all duration-300 ${i === activeDot ? 'w-4 h-2 bg-brand-yellow' : 'w-2 h-2 bg-gray-300'}`} />
          ))}
        </div>
      )}
      {/* Desktop: normal grid */}
      <div className="hidden md:grid max-w-7xl mx-auto px-6 grid-cols-6 gap-6">
        {items.map(cat => cat.skeleton ? (
          <div key={cat.id} className="flex flex-col items-center gap-2">
            <div className="w-36 h-36 rounded-full bg-gray-100 animate-pulse" />
            <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
          </div>
        ) : (
          <Link key={cat.id} to={`/category/${cat.slug || cat.id}`}
            className="flex flex-col items-center gap-2 group">
            <div className="w-36 h-36 rounded-full overflow-hidden bg-brand-cream-light border-2 border-brand-yellow/20 group-hover:border-brand-yellow group-hover:shadow-md transition-all duration-300 group-hover:scale-105">
              {cat.image
                ? <img src={cldImg(cat.image, 300)} alt={cat.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-4xl">{cat.emoji || '🎁'}</div>
              }
            </div>
            <span className="text-base text-center text-gray-700 group-hover:text-brand-dark font-semibold leading-tight">{cat.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ── Deal Slider (mobile carousel) ─────────────────────────────
const SLIDE_TRANSITION = 'transform 0.46s cubic-bezier(0.34, 1.1, 0.64, 1), opacity 0.4s ease';

function DealSlider({ items, addToCart, isWishlisted, toggleWishlist }) {
  const navigate = useNavigate();
  const [activeIdx, setActiveIdx] = useState(0);
  const stageRef = useRef(null);
  const totalRef = useRef(items.length);
  useEffect(() => { totalRef.current = items.length; }, [items.length]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    let startX = null, startY = null;
    const onStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onEnd = (e) => {
      if (startX === null) return;
      const dx = startX - e.changedTouches[0].clientX;
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      if (Math.abs(dx) > 35 && Math.abs(dx) > dy) {
        setActiveIdx(i => dx > 0
          ? Math.min(i + 1, totalRef.current - 1)
          : Math.max(i - 1, 0));
      }
      startX = null;
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend',   onEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend',   onEnd);
    };
  }, []);

  if (!items.length) return null;
  const total = items.length;

  // Left card:  falls lower-left  + counter-clockwise tilt
  // Right card: falls lower-right + clockwise tilt
  const centerStyle = {
    position: 'absolute', left: '50%', top: 0, width: '74vw',
    transition: SLIDE_TRANSITION, willChange: 'transform, opacity',
    transform: 'translateX(-50%) translateY(0px) rotate(0deg) scale(1)',
    opacity: 1, zIndex: 10, cursor: 'pointer',
  };
  const sideStyle = (side) => ({
    position: 'absolute', left: '50%', top: 0, width: '74vw',
    transition: SLIDE_TRANSITION, willChange: 'transform, opacity',
    transform: side === 'left'
      ? 'translateX(calc(-50% - 63vw)) translateY(32px) rotate(-24deg) scale(0.82)'
      : 'translateX(calc(-50% + 63vw)) translateY(32px) rotate(24deg) scale(0.82)',
    opacity: 0.72, zIndex: 4, cursor: 'pointer',
  });

  // No looping — only render cards that actually exist
  const cards = [];
  if (activeIdx > 0)         cards.push({ idx: activeIdx - 1, pos: 'left' });
  cards.push(                { idx: activeIdx,     pos: 'center' });
  if (activeIdx < total - 1) cards.push({ idx: activeIdx + 1, pos: 'right' });

  return (
    <div style={{ position: 'relative', zIndex: 1 }}>
      {/* Warm spotlight glow */}
      <div style={{
        position: 'absolute', top: '32%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '72vw', height: '72vw', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,215,75,0.52) 0%, rgba(255,180,35,0.2) 44%, transparent 70%)',
        filter: 'blur(30px)', pointerEvents: 'none', zIndex: 0,
      }} />

      {/* Card stage — touch listeners attached via ref */}
      <div
        ref={stageRef}
        style={{ position: 'relative', height: 'calc(74vw + 202px)', overflow: 'hidden', zIndex: 1 }}
      >
        {cards.map(({ idx, pos }) => {
          const d = items[idx];
          const p = d?.product;
          if (!p) return null;
          const dealPrice   = parseFloat(d.deal_price);
          const origPrice   = p.original_price ? parseFloat(p.original_price) : null;
          const discount    = origPrice ? Math.round(((origPrice - dealPrice) / origPrice) * 100) : null;
          const image       = p?.images?.[0]?.image_url;
          const hasVariants = p?.variants?.length > 0;
          const wishlisted  = isWishlisted(p?.id);
          const isCenter    = pos === 'center';

          return (
            <div
              key={idx}
              style={isCenter ? centerStyle : sideStyle(pos)}
              onClick={() => { isCenter ? navigate(`/product/${p.slug}`) : setActiveIdx(idx); }}
            >
              <div style={{
                background: '#fff', borderRadius: '22px', overflow: 'hidden',
                boxShadow: isCenter
                  ? '0 20px 55px rgba(0,0,0,0.18), 0 8px 20px rgba(0,0,0,0.09)'
                  : '0 4px 14px rgba(0,0,0,0.08)',
                transition: 'box-shadow 0.42s ease',
              }}>
                {/* Image */}
                <div style={{ position: 'relative', aspectRatio: '1/1', overflow: 'hidden', background: '#f7f5f0' }}>
                  {image
                    ? <img src={cldImg(image, 500)} alt={p.name} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3.5rem' }}>📦</div>
                  }
                  {isCenter && (
                    <button
                      onClick={e => { e.preventDefault(); e.stopPropagation(); toggleWishlist(p.id); }}
                      style={{
                        position: 'absolute', top: 12, right: 12,
                        width: 34, height: 34, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.95)', border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      }}>
                      <Heart style={{ width: 17, height: 17, fill: wishlisted ? '#ef4444' : 'none', color: wishlisted ? '#ef4444' : '#9ca3af' }} />
                    </button>
                  )}
                </div>

                {/* Info — side cards keep same DOM height but hidden */}
                <div style={{ padding: '14px 16px 16px', visibility: isCenter ? 'visible' : 'hidden' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', lineHeight: 1.3, marginBottom: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                    {p.name}
                  </h3>
                  <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: 10, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {p.short_description || p.description?.replace(/<[^>]*>/g, '').slice(0, 80) || p?.category?.name || ''}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 7, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#111827' }}>₹{dealPrice.toLocaleString('en-IN')}</span>
                    {origPrice && <span style={{ fontSize: '0.78rem', color: '#b0b7c3', textDecoration: 'line-through' }}>₹{origPrice.toLocaleString('en-IN')}</span>}
                  </div>
                  {discount && (
                    <div style={{ marginBottom: 12 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', background: '#d1fae5', color: '#065f46', fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>
                        {discount}% OFF
                      </span>
                    </div>
                  )}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      if (hasVariants) { navigate(`/product/${p.slug}`); return; }
                      addToCart(p, null, 1, dealPrice);
                      toast.success('Added to cart!', { icon: '🛒', duration: 1500 });
                    }}
                    style={{
                      width: '100%', padding: '10px 0',
                      border: '1.5px solid #e5e7eb', borderRadius: 14,
                      background: '#fff', color: '#111827',
                      fontSize: '0.85rem', fontWeight: 500,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                    {hasVariants ? 'Choose Options' : 'Add to cart'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dot indicators */}
      {total > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, paddingBottom: 14, position: 'relative', zIndex: 1 }}>
          {items.map((_, i) => (
            <div key={i} onClick={() => setActiveIdx(i)} style={{
              height: 6, borderRadius: 999, cursor: 'pointer',
              width: i === activeIdx ? 22 : 6,
              background: i === activeIdx ? '#1a1a1a' : '#d1d5db',
              transition: 'all 0.32s ease',
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Deal of the Day ────────────────────────────────────────────
function DealOfTheDay({ deals, loading }) {
  const { addToCart, setDrawerOpen } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const location = useLocation();
  const time = useCountdown(deals.length ? deals[0]?.end_time : DEAL_END);
  // While the deals API is still loading, hold the section's height so the page
  // below doesn't jump when (or if) deals appear. Collapse only once we know
  // there are none.
  if (!deals.length) {
    if (!loading) return null;
    return (
      <section className="max-w-7xl mx-auto px-4 py-6" aria-hidden="true">
        <div className="md:hidden" style={{ minHeight: 'calc(74vw + 262px)' }} />
        <div className="hidden md:block" style={{ minHeight: '340px' }} />
      </section>
    );
  }
  const items = deals;

  return (
    <section className="max-w-7xl mx-auto px-4 py-6">
      {/* ── MOBILE LAYOUT ── */}
      <div className="md:hidden bg-brand-cream rounded-3xl overflow-hidden relative">
        {/* Title banner area */}
        <div className="relative pt-4 pb-3 px-4 overflow-hidden">
          {/* Bunting flags top-left */}
          <div className="absolute top-0 left-0 flex items-end gap-0 pointer-events-none" style={{ transform: 'translateY(-2px)' }}>
            {['#c9a84c','#d4b86a','#b8963e','#c9a84c','#d4b86a','#b8963e'].map((color, i) => (
              <div key={i} style={{
                width: 0, height: 0,
                borderLeft: '9px solid transparent',
                borderRight: '9px solid transparent',
                borderTop: `18px solid ${color}`,
                marginRight: 1,
                opacity: 0.85,
              }} />
            ))}
          </div>
          {/* Bunting string */}
          <div className="absolute top-2 left-0 right-0 h-px pointer-events-none" style={{ background: 'linear-gradient(to right, #c9a84c 0%, transparent 60%)', opacity: 0.5 }} />

          {/* Sparkles right */}
          <div className="absolute right-4 top-3 pointer-events-none select-none">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <path d="M36 8 L37.5 14 L43 15 L37.5 16 L36 22 L34.5 16 L29 15 L34.5 14 Z" fill="#c9a84c" opacity="0.7"/>
              <path d="M42 24 L42.8 27 L46 27.5 L42.8 28 L42 31 L41.2 28 L38 27.5 L41.2 27 Z" fill="#c9a84c" opacity="0.5"/>
              <circle cx="28" cy="10" r="2" fill="#c9a84c" opacity="0.4"/>
            </svg>
          </div>
          {/* Dot decorations */}
          <div className="absolute left-16 top-2 w-1.5 h-1.5 rounded-full bg-yellow-600 opacity-30 pointer-events-none" />
          <div className="absolute left-8 top-6 w-1 h-1 rounded-full bg-yellow-700 opacity-25 pointer-events-none" />

          {/* Title */}
          <div className="text-center mt-3 relative z-10">
            <h2 className="leading-none" style={{ fontFamily: 'inherit' }}>
              <span style={{ fontSize: '2.2rem', fontWeight: 900, fontStyle: 'italic', letterSpacing: '-0.5px', color: '#1a1a1a' }}>Deal</span>
              <span style={{ fontSize: '1rem', fontWeight: 600, color: '#1a1a1a', margin: '0 3px' }}>of the</span>
              <span style={{ fontSize: '2.2rem', fontWeight: 900, letterSpacing: '1px', color: '#1a1a1a' }}>DAY</span>
              <span style={{ fontSize: '1rem', fontWeight: 900, color: '#1a1a1a', marginLeft: '2px' }}>.</span>
            </h2>
          </div>
        </div>

        {/* Countdown */}
        <div className="flex items-center justify-center gap-1 pb-4 px-4 relative z-10">
          <span className="text-sm text-gray-500 font-medium mr-1">Ends In</span>
          {[{ val: time.h, unit: 'h' }, { val: time.m, unit: 'm' }, { val: time.s, unit: 's' }].map((t, i) => (
            <span key={i} className="flex items-baseline gap-0.5">
              <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1a1a1a' }}>{t.val}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1a1a1a' }}>{t.unit}</span>
              {i < 2 && <span style={{ fontSize: '1rem', fontWeight: 800, color: '#1a1a1a', margin: '0 4px' }}>:</span>}
            </span>
          ))}
        </div>

        {/* Slider carousel — snap-centered with peek on both sides */}
        <DealSlider items={items} addToCart={addToCart} isWishlisted={isWishlisted} toggleWishlist={toggleWishlist} />

        {/* View All button */}
        <div className="text-center pb-5 relative z-10">
          <Link to="/deals" className="inline-block border-2 border-brand-dark text-brand-dark text-sm font-bold px-8 py-2.5 rounded-full hover:bg-brand-dark hover:text-white transition-colors">
            View All products
          </Link>
        </div>
      </div>

      {/* ── DESKTOP LAYOUT (unchanged) ── */}
      <div className="hidden md:block bg-brand-cream rounded-3xl p-8 overflow-hidden relative">
        {/* Sunburst decoration */}
        <div className="absolute right-4 top-4 w-48 h-48 opacity-10 pointer-events-none z-0">
          {Array.from({ length: 16 }).map((_, i) => (
            <div key={i} className="absolute top-1/2 left-1/2 w-1.5 h-36 bg-brand-dark origin-bottom"
              style={{ transform: `rotate(${i * 22.5}deg) translateX(-50%)` }} />
          ))}
        </div>

        <div className="flex flex-row items-start gap-6">
          <div className="flex-shrink-0 w-48">
            <span className="inline-block bg-brand-red text-white text-xs font-black px-3 py-1 rounded-full mb-2 uppercase tracking-wider">Limited Time</span>
            <h2 className="text-3xl font-black text-brand-dark mb-4 leading-tight">Deal<br />of the Day</h2>
            <div className="flex items-center gap-1.5 mb-2">
              <Clock className="w-4 h-4 text-brand-red flex-shrink-0" />
              <span className="text-xs text-gray-500 font-medium">Ends in</span>
            </div>
            <div className="flex gap-1">
              {[{ val: time.h, label: 'HRS' }, { val: time.m, label: 'MIN' }, { val: time.s, label: 'SEC' }].map((t, i) => (
                <div key={i} className="flex items-end gap-1">
                  <div className="text-center">
                    <div className="bg-brand-dark text-white text-lg font-black w-11 h-11 rounded-xl flex items-center justify-center">{t.val}</div>
                    <p className="text-xs text-gray-400 mt-0.5">{t.label}</p>
                  </div>
                  {i < 2 && <span className="text-brand-dark font-black text-xl mb-4">:</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 flex-1 relative z-10">
            {items.map((d, i) => {
              const p = d.product;
              const dealPrice = parseFloat(d.deal_price);
              const image = p?.images?.[0]?.image_url;
              const hasVariants = p?.variants?.length > 0;
              const wishlisted = isWishlisted(p?.id);
              return (
                <Link key={d.id || i} to={`/product/${p?.slug || '#'}`}
                  className="group flex-shrink-0 w-40 block"
                  data-pcard={`deals:${p?.id}`}
                  onClick={(e) => rememberAnchor(location.key, `deals:${p?.id}`, e.currentTarget.getBoundingClientRect().top + window.scrollY)}>
                  <div className="card-hover bg-white rounded-2xl overflow-hidden border border-gray-100">
                    <div className="relative aspect-square overflow-hidden bg-gray-50">
                      {image
                        ? <img src={cldImg(image, 400)} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        : <div className="w-full h-full flex items-center justify-center bg-brand-cream-light"><span className="text-4xl">📦</span></div>
                      }
                      <div className="absolute top-2 left-2">
                        <span className="badge-sale">Deal</span>
                      </div>
                      <button onClick={e => { e.preventDefault(); e.stopPropagation(); toggleWishlist(p.id); }}
                        className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center hover:scale-110 transition-all active:scale-95">
                        <Heart className={`w-4 h-4 transition-colors ${wishlisted ? 'fill-brand-red text-brand-red' : 'text-gray-400'}`} />
                      </button>
                      <button
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (hasVariants) { window.location.href = `/product/${p?.slug}`; return; }
                          addToCart(p, null, 1, dealPrice);
                          setDrawerOpen(true);
                        }}
                        className="absolute bottom-0 left-0 right-0 bg-brand-yellow text-brand-dark text-xs font-bold py-2.5 flex items-center justify-center gap-1.5
                          translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <ShoppingBag className="w-3.5 h-3.5" />
                        {hasVariants ? 'Choose Options' : 'Add to Cart'}
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-gray-400 mb-0.5">{p?.category?.name || 'Deal'}</p>
                      <h3 className="text-sm font-semibold text-brand-dark leading-tight line-clamp-2 mb-1.5">{p?.name}</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-base font-bold text-brand-dark">₹{dealPrice}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Promo Banners ──────────────────────────────────────────────
function PromoBannerCard({ b, big = false }) {
  const content = (
    <div className={`relative rounded-2xl overflow-hidden group block w-full h-full ${big ? 'aspect-[2/1] md:aspect-auto md:min-h-[360px]' : 'aspect-square md:aspect-auto md:min-h-[172px]'}`}>
      {b.image_url
        ? <picture className="absolute inset-0 w-full h-full">
            {b.mobile_image_url && <source media="(max-width: 767px)" srcSet={cldImg(b.mobile_image_url, 768)} />}
            <img src={cldImg(b.image_url, 800)} alt={b.title || ''} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </picture>
        : <div className={`absolute inset-0 bg-gradient-to-br ${b.bg || 'from-gray-700 to-gray-900'}`} />
      }
      {/* overlay — only if there's text to show */}
      {(b.title || b.subtitle || b.button_text) && !b.image_url && (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
          <div className={`relative z-10 h-full flex flex-col justify-end ${big ? 'p-6 md:p-8' : 'p-4 md:p-5'}`}>
            {b.subtitle && <p className={`font-semibold uppercase tracking-widest text-white/80 mb-1 ${big ? 'text-xs md:text-sm' : 'text-[10px] md:text-xs'}`}>{b.subtitle}</p>}
            {b.title && <h3 className={`font-black text-white leading-tight mb-3 ${big ? 'text-xl md:text-3xl' : 'text-sm md:text-lg'}`}>{b.title}</h3>}
            {b.button_text && <span className={`bg-white text-brand-dark font-bold rounded-full inline-block transition-all group-hover:bg-brand-yellow ${big ? 'px-5 py-2 text-sm' : 'px-3 py-1.5 text-xs'}`}>{b.button_text}</span>}
          </div>
        </>
      )}
      {/* emoji decoration when no image */}
      {!b.image_url && b.emoji && (
        <span className={`absolute bottom-4 right-4 select-none opacity-30 ${big ? 'text-8xl' : 'text-5xl'}`}>{b.emoji}</span>
      )}
    </div>
  );

  return (
    <Link to={b.link || '/products'} className="block h-full">
      {content}
    </Link>
  );
}

function PromoBanners({ banners, loading }) {
  // While banners load, hold the slot with a neutral skeleton (no fake content).
  if (loading && !banners.length) {
    return (
      <section className="max-w-7xl mx-auto px-4 py-6">
        <div className="md:hidden flex flex-col gap-3">
          <div className="aspect-[2/1] rounded-2xl bg-gray-100 animate-pulse" />
          <div className="grid grid-cols-2 gap-3">
            <div className="aspect-square rounded-2xl bg-gray-100 animate-pulse" />
            <div className="aspect-square rounded-2xl bg-gray-100 animate-pulse" />
          </div>
        </div>
        <div className="hidden md:grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 md:row-span-2 min-h-[360px] rounded-2xl bg-gray-100 animate-pulse" />
          <div className="min-h-[172px] rounded-2xl bg-gray-100 animate-pulse" />
          <div className="min-h-[172px] rounded-2xl bg-gray-100 animate-pulse" />
        </div>
      </section>
    );
  }

  const sorted = [...banners].sort((a, b) => a.sort_order - b.sort_order);
  const main = sorted[0];
  const sub1 = sorted[1];
  const sub2 = sorted[2];

  if (!main) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 py-6">
      {/* Mobile layout: big full-width, then 2 side by side */}
      <div className="md:hidden flex flex-col gap-3">
        <PromoBannerCard b={main} big />
        {(sub1 || sub2) && (
          <div className="grid grid-cols-2 gap-3">
            {sub1 && <PromoBannerCard b={sub1} />}
            {sub2 && <PromoBannerCard b={sub2} />}
          </div>
        )}
      </div>
      {/* Desktop layout: left 2/3 big + right 1/3 two stacked */}
      <div className="hidden md:grid md:grid-cols-3 gap-4" style={{ gridTemplateRows: 'auto' }}>
        <div className="md:col-span-2 md:row-span-2">
          <PromoBannerCard b={main} big />
        </div>
        {sub1 && <div><PromoBannerCard b={sub1} /></div>}
        {sub2 && <div><PromoBannerCard b={sub2} /></div>}
      </div>
    </section>
  );
}

// ── Products Row ───────────────────────────────────────────────
function ProductCardSkeleton() {
  return (
    <div className="flex-shrink-0 w-44 md:w-52">
      <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
        <div className="aspect-square bg-gray-100 animate-pulse" />
        <div className="p-3 space-y-2">
          <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse" />
          <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
          <div className="h-5 w-1/3 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function ProductsRow({ label, title, apiProducts, viewAllHref, badge, loading }) {
  const items = apiProducts || [];
  // Loaded with no products — hide the whole section (no fake fallback).
  if (!loading && !items.length) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 py-6">
      <SectionHeader label={label} title={title} viewAllHref={viewAllHref} />
      <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
        {loading && !items.length
          ? Array.from({ length: 5 }).map((_, i) => <ProductCardSkeleton key={i} />)
          : items.map(p => (
              <div key={p.id} className="flex-shrink-0 w-44 md:w-52"><ProductCard product={p} badge={badge} rowId={title} /></div>
            ))
        }
      </div>
    </section>
  );
}

// ── Top Categories Grid ────────────────────────────────────────
function ExploreCategories({ categories, loading }) {
  const showSkeleton = loading && !categories.length;
  const items = showSkeleton ? SKELETON_ITEMS : categories;
  if (!items.length) return null;
  return (
    <section className="py-8">
      <div className="max-w-7xl mx-auto px-4">
        <SectionHeader title="Shop by Category" viewAllHref="/products" />
      </div>
      {/* Mobile: 2-row horizontal scroll */}
      <div className="md:hidden overflow-x-auto scrollbar-hide px-3">
        <div className="grid grid-rows-2 grid-flow-col gap-x-4 gap-y-3 w-max">
          {items.map(cat => cat.skeleton ? (
            <div key={cat.id} className="flex flex-col items-center gap-2 w-28">
              <div className="w-24 h-24 rounded-full bg-gray-100 animate-pulse" />
              <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
            </div>
          ) : (
            <Link key={cat.id} to={`/category/${cat.slug || cat.id}`}
              className="flex flex-col items-center gap-2 group w-28">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-brand-cream-light border-2 border-brand-yellow/20 group-hover:border-brand-yellow transition-all duration-300">
                {cat.image
                  ? <img src={cldImg(cat.image, 300)} alt={cat.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-3xl">{cat.emoji || '🎁'}</div>
                }
              </div>
              <span className="text-xs text-center text-gray-700 font-semibold leading-tight w-full">{cat.name}</span>
            </Link>
          ))}
        </div>
      </div>
      {/* Desktop: normal grid */}
      <div className="hidden md:grid max-w-7xl mx-auto px-6 grid-cols-6 gap-6">
        {items.map(cat => cat.skeleton ? (
          <div key={cat.id} className="flex flex-col items-center gap-2">
            <div className="w-36 h-36 rounded-full bg-gray-100 animate-pulse" />
            <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
          </div>
        ) : (
          <Link key={cat.id} to={`/category/${cat.slug || cat.id}`}
            className="group flex flex-col items-center gap-2 text-center">
            <div className="w-36 h-36 rounded-full overflow-hidden bg-brand-cream-light border-2 border-brand-yellow/20 group-hover:border-brand-yellow group-hover:shadow-md transition-all duration-300 group-hover:scale-105">
              {cat.image
                ? <img src={cldImg(cat.image, 300)} alt={cat.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                : <span className="text-4xl">{cat.emoji || '🎁'}</span>
              }
            </div>
            <span className="text-base text-center text-gray-700 group-hover:text-brand-dark font-semibold leading-tight">{cat.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ── Main HomePage ──────────────────────────────────────────────
export default function HomePage() {
  const whatsappNumber = useWhatsAppNumber();
  // Hydrate from cache on Back/Forward so the page paints instantly (no refetch
  // flash) and scroll restoration can land on the clicked card seamlessly.
  const cached = getPageCache('home');
  const [heroBanners, setHeroBanners] = useState(cached?.heroBanners || []);
  const [promoBanners, setPromoBanners] = useState(cached?.promoBanners || []);
  const [categories, setCategories] = useState(cached?.categories || []);
  const [flashDeals, setFlashDeals] = useState(cached?.flashDeals || []);
  const [featuredProducts, setFeaturedProducts] = useState(cached?.featuredProducts || []);
  const [bestSellers, setBestSellers] = useState(cached?.bestSellers || []);
  const [stockClearance, setStockClearance] = useState(cached?.stockClearance || []);
  const [customSections, setCustomSections] = useState(cached?.customSections || []);
  // The exact product ids the admin curated for each homepage row, so the row's
  // "View All" shows ONLY those products (not all products sorted).
  const [sectionIds, setSectionIds] = useState(cached?.sectionIds || { new: [], best: [], clear: [] });
  const [dealsLoaded, setDealsLoaded] = useState(!!cached);
  const [bannersLoaded, setBannersLoaded] = useState(!!cached);
  const [categoriesLoaded, setCategoriesLoaded] = useState(!!cached);
  const [promosLoaded, setPromosLoaded] = useState(!!cached);
  const [productsLoaded, setProductsLoaded] = useState(!!cached);

  useEffect(() => {
    Promise.allSettled([
      api.get('/banners?type=hero'),
      api.get('/banners?type=promo'),
      api.get('/categories?homepage=1'),
      api.get('/flash-deals/active'),
      api.get('/settings/public'),
    ]).then(async ([heroRes, promoRes, catRes, dealsRes, settingsRes]) => {
      const hero = heroRes.status === 'fulfilled' ? heroRes.value.data.banners || [] : [];
      const promo = promoRes.status === 'fulfilled' ? promoRes.value.data.banners || [] : [];
      const cats = catRes.status === 'fulfilled' ? catRes.value.data.categories || [] : [];
      const deals = dealsRes.status === 'fulfilled' ? dealsRes.value.data.deals || [] : [];
      setHeroBanners(hero); setBannersLoaded(true);
      setPromoBanners(promo); setPromosLoaded(true);
      setCategories(cats); setCategoriesLoaded(true);
      setFlashDeals(deals); setDealsLoaded(true);

      const s = settingsRes.status === 'fulfilled' ? settingsRes.value.data.settings || {} : {};
      const newIds = s.homepage_new_arrivals ? JSON.parse(s.homepage_new_arrivals) : [];
      const bestIds = s.homepage_best_sellers ? JSON.parse(s.homepage_best_sellers) : [];
      const clearIds = s.homepage_stock_clearance ? JSON.parse(s.homepage_stock_clearance) : [];
      let sections = [];
      try { sections = s.homepage_custom_sections ? JSON.parse(s.homepage_custom_sections) : []; } catch(e) { sections = []; }
      setCustomSections(sections);
      setSectionIds({ new: newIds, best: bestIds, clear: clearIds });

      const [newRes, bestRes, clearRes] = await Promise.allSettled([
        newIds.length > 0 ? api.get('/products', { params: { ids: newIds.join(','), limit: 12 } }) : api.get('/products?limit=12&sort=newest'),
        bestIds.length > 0 ? api.get('/products', { params: { ids: bestIds.join(','), limit: 12 } }) : api.get('/products?limit=12&sort=popular'),
        clearIds.length > 0 ? api.get('/products', { params: { ids: clearIds.join(','), limit: 12 } }) : Promise.resolve({ data: { products: [] } }),
      ]);
      const featured = newRes.status === 'fulfilled' ? newRes.value.data.products || [] : [];
      const best = bestRes.status === 'fulfilled' ? bestRes.value.data.products || [] : [];
      const clear = clearRes.status === 'fulfilled' ? clearRes.value.data.products || [] : [];
      setFeaturedProducts(featured); setBestSellers(best); setStockClearance(clear);
      setProductsLoaded(true);

      setPageCache('home', {
        heroBanners: hero, promoBanners: promo, categories: cats, flashDeals: deals,
        customSections: sections, featuredProducts: featured, bestSellers: best, stockClearance: clear,
        sectionIds: { new: newIds, best: bestIds, clear: clearIds },
      });
    });
  }, []);

  return (
    <div className="animate-fade-in">
      <HeroSlider apiBanners={heroBanners} loading={!bannersLoaded} />
      <CategoryRow categories={categories} loading={!categoriesLoaded} />
      <DealOfTheDay deals={flashDeals} loading={!dealsLoaded} />
      <PromoBanners banners={promoBanners} loading={!promosLoaded} />
      <PriceCategories />
      <ProductsRow label="Just Dropped" title="New Arrivals"
        apiProducts={featuredProducts} loading={!productsLoaded}
        viewAllHref={sectionIds.new?.length ? `/products?ids=${sectionIds.new.join(',')}` : '/products?sort=createdAt-DESC'}
        badge="new-arrival" />
      <ProductsRow label="Fan Favourites" title="Best Sellers"
        apiProducts={bestSellers} loading={!productsLoaded}
        viewAllHref={sectionIds.best?.length ? `/products?ids=${sectionIds.best.join(',')}` : '/products?sort=popular-DESC'}
        badge="best-seller" />
      {stockClearance.length > 0 && (
        <ProductsRow label="Limited Time" title="Stock Clearance"
          products={[]} apiProducts={stockClearance}
          viewAllHref={`/products?ids=${(sectionIds.clear?.length ? sectionIds.clear : stockClearance.map(p => p.id)).join(',')}`}
          badge="clearance" />
      )}
      <ExploreCategories categories={categories} loading={!categoriesLoaded} />

      {/* Bottom CTA */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-brand-dark rounded-3xl p-8 md:p-14 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 text-9xl opacity-10 select-none leading-none">🎌</div>
          <p className="text-brand-yellow text-xs font-black uppercase tracking-widest mb-3">Curated for fans</p>
          <h2 className="text-white font-black text-2xl md:text-4xl mb-3">Can't find what you're looking for?</h2>
          <p className="text-gray-400 mb-7 max-w-md mx-auto">We do custom orders — anime figures, personalized gifts, cake toppers & more. Just WhatsApp us!</p>
          <a href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi! I want a custom order")}`}
            target="_blank" rel="noreferrer"
            className="btn-primary rounded-full px-10 py-3.5 text-base inline-block">
            WhatsApp Us 💬
          </a>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-brand-dark mb-2">Why 1000+ choose TheYellove</h2>
          <p className="text-gray-500">Made with Love, For the Ones You Love ❤️</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
          {[
            { Icon: Package,       color: '#d97706', bg: '#FEF3C7', title: 'Carefully packed',    desc: 'Bubble wrapped & box packed for safety' },
            { Icon: Award,         color: '#059669', bg: '#D1FAE5', title: 'Quality checked',     desc: 'Every product inspected before shipping' },
            { Icon: Heart,         color: '#3b82f6', bg: '#DBEAFE', title: 'Made with love',      desc: 'Small Chennai brand, big on quality' },
            { Icon: Users,         color: '#db2777', bg: '#FCE7F3', title: '1000+ Happy Customers', desc: 'Growing every day with love' },
            { Icon: WhatsAppIcon,  color: '#25D366', bg: '#D1FAE5', title: 'Real human support',  desc: 'WhatsApp us anytime, we reply fast' },
            { Icon: FileText,      color: '#d97706', bg: '#FEF3C7', title: 'GST registered',      desc: 'Official invoice with every order' },
          ].map(({ Icon, color, bg, title, desc }, i) => (
            <div key={i} className="flex flex-col items-center text-center gap-3">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: bg }}>
                <Icon className="w-7 h-7" style={{ color }} strokeWidth={1.8} />
              </div>
              <div>
                <p className="font-bold text-brand-dark text-sm md:text-base">{title}</p>
                <p className="text-gray-500 text-xs md:text-sm mt-0.5">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}
